import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Sidebar from '../../components/SideBar';
import NavBar from '../../components/NavBarSearchSong';
import Settings from '../../assets/Settings.png';
import email_icn from '../../assets/email.svg';
import call from '../../assets/call.svg';
import RoundButton from '../../components/RoundButton';
import './NewEditor.scss';
import { _showAlert } from '../../utils';
import { createAndUpdateEditor, useGetEditors } from '../../api/editor';

function NewEditor(props) {
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const { mutateEditors } = useGetEditors();

  const defaultFormState = {
    name: '',
    username: '',
    email: '',
    password: '',
    address: '',
    phone: '',
    firstname: '',
    lastname: '',
  };
  const [formFields, setFormFields] = useState(defaultFormState);
  const [togglePassword, setTogglePassword] = useState(true);
  const [errors, setErrors] = useState();
  const [readoOnlyFields, setReadOnlyFields] = useState({
    username: true,
    password: true,
  });
  const [isChangePasswordActive, setIsChangePasswordActive] = useState(false);

  useEffect(() => {
    // Check if we have editor data from the location state
    if (location.state?.editorData) {
      const editorData = location.state.editorData;
      setFormFields({
        id: editorData.id,
        name: editorData.name || '',
        username: editorData.username || '',
        email: editorData.email || '',
        password: '', // Don't populate password for security
        address: editorData.address || '',
        phone: editorData.phone || '',
        firstname: editorData.firstname || '',
        lastname: editorData.lastname || '',
      });
    }
  }, [location.state]);

  /**
   * it will handle the validation.
   */
  const handleValidation = () => {
    let fields = formFields;
    let errors = {};
    let formIsValid = true;

    if (!fields['username']) {
      formIsValid = false;
      errors['username'] = 'Username is required';
    }
    if (!fields['email']) {
      formIsValid = false;
      errors['email'] = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(fields['email'])) {
      formIsValid = false;
      errors['email'] = 'Please enter a valid email address';
    }
    if (!formFields.id && !fields['password']) {
      formIsValid = false;
      errors['password'] = 'Password is required for new editors';
    }
    if (isChangePasswordActive && !fields['password']) {
      formIsValid = false;
      errors['password'] = 'Password is required';
    }

    setErrors(errors);
    return formIsValid;
  };

  const handleInputChange = (event) => {
    let id = event.target.id;
    let value = event.target.value;
    if (id === 'password') {
      setIsChangePasswordActive(true);
    }
    setFormFields({ ...formFields, [id]: value });
  };

  /**
   * it will handle the new customer api request.
   */
  const handleNewEditor = async () => {
    if (handleValidation()) {
      setIsLoading(true);
      let payload = {
        firstname: formFields.firstname.trim(),
        lastname: formFields.lastname.trim(),
        name: formFields.name.trim(),
        username: formFields.username.trim(),
        phone: formFields.phone.trim(),
        email: formFields.email.trim(),
        address: formFields.address.trim(),
      };
      // Only include password if it's a new editor or if change password is active
      if (!formFields.id || (formFields.id && isChangePasswordActive)) {
        payload.password = formFields.password;
      }

      // Add id to payload if it exists (for updates)
      if (formFields.id) {
        payload.id = formFields.id;
      }

      try {
        await createAndUpdateEditor(payload);
        _showAlert(formFields.id ? 'Editor updated successfully' : 'Editor created successfully', 'success', 4000);
        setFormFields(defaultFormState);
        // Call mutateEditors to refresh the editors list
        await mutateEditors();
        history.push('/editors');
      } catch (error) {
        _showAlert(error?.response?.data?.message || 'Error processing editor', 'error', 4000);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelPasswordChange = () => {
    setIsChangePasswordActive(false);
    setReadOnlyFields({ ...readoOnlyFields, password: true });
    setFormFields({ ...formFields, password: '' });
  };

  return (
    <>
      <div className="main">
        <div className="sidebar">
          <Sidebar />
        </div>
        <div className="leftPortion">
          <NavBar
            data={formFields.id ? 'Update Editor' : 'New Editor'}
            icon={Settings}
            currentRouteName="Editors"
            breadcrumbsPath={formFields.id ? 'Update Editor' : 'New Editor'}
          />
          <div className="content">
            <div className="btn_row">
              <RoundButton disabled={isLoading} btnText={isLoading ? 'Saving...' : 'Save'} handleClick={handleNewEditor} />
            </div>
            <div className="new_customer_row">
              <h5>General</h5>
              <div className="field_row">
                <div className="field_col">
                  <label>First Name</label>
                  <input type="text" value={formFields.firstname} onChange={handleInputChange} id="firstname" />
                  <span className="input-error-messages">{errors && errors['firstname']}</span>
                </div>
                <div className="field_col">
                  <label>Last Name</label>
                  <input type="text" value={formFields.lastname} onChange={handleInputChange} id="lastname" />
                  <span className="input-error-messages">{errors && errors['lastname']}</span>
                </div>
                <div className="field_col">
                  <label> Name</label>
                  <input type="text" value={formFields.name} onChange={handleInputChange} id="name" />
                  <span className="input-error-messages">{errors && errors['name']}</span>
                </div>
                <div className="field_col">
                  <label>Username</label>
                  <input
                    type="text"
                    value={formFields.username}
                    onChange={handleInputChange}
                    id="username"
                    readOnly={readoOnlyFields.username}
                    onFocus={() => setReadOnlyFields({ ...readoOnlyFields, username: false })}
                  />
                  <span className="input-error-messages">{errors && errors['username']}</span>
                </div>
                <div className="field_col">
                  <label>Password</label>
                  <input
                    type={togglePassword ? 'password' : 'text'}
                    value={formFields.password}
                    onChange={handleInputChange}
                    id="password"
                    readOnly={readoOnlyFields.password}
                    onFocus={() => setReadOnlyFields({ ...readoOnlyFields, password: false })}
                  />
                  {formFields.id ? (
                    isChangePasswordActive ? (
                      <>
                        <span
                          role="button"
                          onClick={() => setTogglePassword(!togglePassword)}
                          className="editor_show_password"
                        >
                          {togglePassword ? 'Show' : 'Hide'}
                        </span>
                        <span
                          role="button"
                          onClick={handleCancelPasswordChange}
                          className="cancel_password"
                        >
                          Cancel
                        </span>
                      </>
                    ) : (
                      <span
                        role="button"
                        onClick={() => {
                          setReadOnlyFields({ ...readoOnlyFields, password: false });
                          setFormFields({ ...formFields, password: '' });
                          setIsChangePasswordActive(true);
                        }}
                        className="show_password change_password"
                      >
                        Change Password
                      </span>
                    )
                  ) : (
                    <span
                      role="button"
                      onClick={() => setTogglePassword(!togglePassword)}
                      className="show_password"
                    >
                      {togglePassword ? 'Show' : 'Hide'}
                    </span>
                  )}
                  <span className="input-error-messages">{errors && errors['password']}</span>
                </div>
              </div>

              <h5>contact</h5>

              <div className="field_row">
                <div className="field_col icn_field">
                  <label>MobilePhone</label>
                  <input type="text" value={formFields.phone} onChange={handleInputChange} id="phone" />
                  <img src={call} alt="call" />
                  <span className="input-error-messages">{errors && errors['phone']}</span>
                </div>
                <div className="field_col icn_field">
                  <label>Email</label>
                  <input type="text" value={formFields.email} onChange={handleInputChange} id="email" />
                  <img src={email_icn} alt="email" />
                  <span className="input-error-messages">{errors && errors['email']}</span>
                </div>
              </div>

              <div className="field_row">
                <div className="field_col">
                  <label>Address</label>
                  <input
                    type="text"
                    placeholder="Franklin Avenue St. Corner"
                    value={formFields.address}
                    onChange={handleInputChange}
                    id="address"
                  />
                  <span className="input-error-messages">{errors && errors['address']}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewEditor;

